import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import {
  Users,
  X,
  Wallet,
  Briefcase,
  Percent,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { USE_MOCK_AUTH } from "../../config/api";
import { CUSTOMERS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import {
  MOCK_CASES,
  MOCK_CUSTOMERS,
  formatCurrency,
} from "../../data/mock/dashboardData";
import {
  EMPTY_DEBT_CUSTOMERS_SUMMARY,
  fetchDebtCustomers,
  fetchDebtCustomersSummary,
} from "../../services/debtCustomersApi";
import { personNameColumns } from "../../utils/personName";
import { showError } from "../../utils/toast";

function enrichMockCustomer(customer) {
  const cases = MOCK_CASES.filter((c) => c.customer === customer.name);
  const primary = cases[0];
  return {
    ...customer,
    accountNumber: customer.accountNumber || `ACC-${customer.id || customer.accountNumber || "—"}`,
    caseId: primary?.id ?? "—",
    outstandingAmount: Math.max(0, customer.amountOwed - customer.amountPaid),
    phone: customer.phone || "",
    updatedAt: primary?.updatedAt ?? primary?.createdAt ?? customer.updatedAt ?? null,
  };
}

function mockSummaryFromCustomers(rows) {
  const customers = rows.length;
  const totalOwed = rows.reduce((sum, row) => sum + (Number(row.amountOwed) || 0), 0);
  const totalPaid = rows.reduce((sum, row) => sum + (Number(row.amountPaid) || 0), 0);
  return {
    customers,
    totalOwed,
    totalPaid,
    totalOutstanding: rows.reduce((sum, row) => sum + (Number(row.outstandingAmount) || 0), 0),
    avgDaysLate: 0,
    shareOfAllCustomers: 100,
  };
}

export default function CustomersPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");

  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(EMPTY_DEBT_CUSTOMERS_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadingMore(false);
      setLoadError(null);
      let shown = false;
      try {
        if (!useLiveApi) {
          const rows = MOCK_CUSTOMERS.map(enrichMockCustomer);
          if (!cancelled) {
            setCustomers(rows);
            setSummary(mockSummaryFromCustomers(rows));
          }
          return;
        }

        const summaryPromise = fetchDebtCustomersSummary(token).catch(() => null);
        const first = await fetchDebtCustomers(token, { page: 1, limit: 50 });
        if (cancelled) return;

        const firstRows = first?.customers || [];
        setCustomers(firstRows);
        shown = true;
        setLoading(false);

        const summary = await summaryPromise;
        if (!cancelled && summary) setSummary(summary);
        else if (!cancelled) setSummary(mockSummaryFromCustomers(firstRows));

        const lastPage = Number(first?.pagination?.last_page || 1);
        if (lastPage > 1) {
          setLoadingMore(true);
          const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, index) =>
              fetchDebtCustomers(token, { page: index + 2, limit: 50 })
            )
          );
          if (cancelled) return;
          const more = rest.flatMap((page) => page?.customers || []);
          setCustomers((current) => current.concat(more));
          setLoadingMore(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || "Could not load customers.");
          showError(err.message || "Could not load customers.");
          if (!shown) {
            setCustomers([]);
            setSummary(EMPTY_DEBT_CUSTOMERS_SUMMARY);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const displayCustomers = useMemo(() => {
    if (!customerParam) return customers;
    const needle = customerParam.trim().toLowerCase();
    return customers.filter((c) => String(c.name || "").toLowerCase() === needle);
  }, [customers, customerParam]);

  const columns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "updatedAt", header: "Date & time", type: "datetime" },
    { key: "accountNumber", header: "Account number", type: "id" },
    ...personNameColumns(),
    { key: "customer_number", header: "Customer no.", type: "phone" },
    { key: "phone", header: "Mobile", type: "phone" },
    { key: "email", header: "Email", type: "text" },
    { key: "ghana_card_number", header: "Ghana Card", type: "id" },
    { key: "gender", header: "Gender", type: "text" },
    { key: "region", header: "Region", type: "text" },
    { key: "country", header: "Country", type: "text" },
    { key: "exact_area", header: "Exact area", type: "text" },
    { key: "caseId", header: "Case ID", type: "id" },
    { key: "cases_count", header: "Cases", type: "number" },
    { key: "loan_amount", header: "Loan amount", type: "currency" },
    { key: "amountOwed", header: "Amount owed", type: "currency" },
    { key: "amountPaid", header: "Amount paid", type: "currency" },
    { key: "outstandingAmount", header: "Outstanding", type: "currency" },
    { key: "dueDate", header: "Due date", type: "date" },
    { key: "daysOverdue", header: "Days overdue", type: "number" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      headerClassName: "text-center",
      actions: (row) => [
        {
          key: "cases",
          label: "View collection cases",
          description: "Recovery cases for this customer",
          icon: Briefcase,
          tone: "primary",
          to: row.debt_customer_slug
            ? `/dashboard/cases?debtCustomer=${encodeURIComponent(row.debt_customer_slug)}${row.name ? `&customer=${encodeURIComponent(row.name)}` : ""}`
            : row.name
              ? `/dashboard/cases?customer=${encodeURIComponent(row.name)}`
              : "/dashboard/cases",
        },
        {
          key: "payments",
          label: "View payments",
          description: "Payment history",
          icon: Wallet,
          tone: "emerald",
          to: row.name
            ? `/dashboard/payments?customer=${encodeURIComponent(row.name)}`
            : "/dashboard/payments",
        },
      ],
    },
  ];

  return (
    <PageShell
      title="Customers"
      description="Unique debtors by account number — KYC details plus owed, paid, and ageing totals."
    >
      {customerParam && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <Users className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Filtered customer</p>
              <p className="text-sm font-bold text-primary">{customerParam}</p>
            </div>
          </div>
          <Link
            to="/dashboard/customers"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-primary/70 ring-1 ring-primary/10 transition-colors hover:text-primary"
          >
            <X className="h-3.5 w-3.5" />
            Show all customers
          </Link>
        </div>
      )}

      <StatCardGrid cols={5} className="mb-4">
        <StatCard
          label="Customers"
          value={summary.customers.toLocaleString()}
          icon={Users}
          tone="primary"
        />
        <StatCard
          label="Total owed"
          value={formatCurrency(summary.totalOwed)}
          icon={Wallet}
          tone="accent"
        />
        <StatCard
          label="Total paid"
          value={formatCurrency(summary.totalPaid)}
          icon={Briefcase}
          tone="emerald"
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(summary.totalOutstanding)}
          icon={Wallet}
          tone="sky"
        />
        <StatCard
          label="Avg days overdue"
          value={Number(summary.avgDaysLate || 0).toLocaleString()}
          icon={Percent}
          tone="accent"
        />
      </StatCardGrid>

      {loadError ? <p className="mb-3 text-sm text-rose-600">{loadError}</p> : null}
      {loadingMore ? (
        <p className="mb-3 text-sm text-primary/45">Loading the rest of the customers…</p>
      ) : null}
      {loading && customers.length === 0 ? (
        <p className="mb-3 text-sm text-primary/45">Loading customers…</p>
      ) : (
        <FilteredDataTable
          key={customerParam ?? "all"}
          data={displayCustomers}
          columns={columns}
          config={{ ...CUSTOMERS_FILTER_CONFIG, title: "Customer filters" }}
          pageSize={15}
        />
      )}
    </PageShell>
  );
}
